import { CSSProperties } from "react";
import { useGameOpt } from "~/app";
import {
  ActionSlot,
  AgentEffectDescription,
  Meaple,
  Player,
  ResourceType,
} from "~/business";
import { useSelection } from "~/jsx/context/SelectionContext";
import { useViewingPlayerOpt } from "~/jsx/hook";
import { Colors, FontSize, Spacing } from "~/jsx/theme";
import { Duration, transition } from "~/jsx/theme/Transition";
import { ZIndex } from "~/jsx/theme/ZIndex";
import { parseEnumOrError } from "~/utils";
import { XAgentEffect } from "./XAgentEffect";
import { XAgentIcon } from "./XAgentIcon";
import { XMeaple } from "./XMeaple";
import { XOverlay } from "./XOverlay";
import { XPlayerIcon } from "./XPlayerIcon";
import { XResourceWithAmount } from "./XResourceWithAmount";
import { XTypography } from "./XTypography";
import { getActionSlotColor } from "./getActionSlotColor";
import { XArrowSvg, XCrossedSwordsSvg, XDreadnought, XLipsSvg } from "./svg";

const shownNames = ["High Council", "Heighliner"];

export const XActionSlot = ({
  actionSlot,
  onClick,
  mentatOpt,
  style,
  noAnimation,
  allowOverlay = false,
  noShadow = false,
}: {
  actionSlot: ActionSlot;
  onClick?: () => void;
  mentatOpt?: Meaple;
  style?: CSSProperties;
  noAnimation?: boolean;
  allowOverlay?: boolean;
  noShadow?: boolean;
}) => {
  const { game } = useGameOpt() ?? {};
  const { players = [] } = game ?? {};
  let oncePerGamePlayedBy: Player[] = [];
  if (actionSlot.id === "High Council") {
    oncePerGamePlayedBy = players.filter((player) => player.hasSiege);
  } else if (actionSlot.id === "Swordmaster") {
    oncePerGamePlayedBy = players.filter((player) => player.hasExtraMeaple);
  }

  const { actionSlot: selectedActionSlot } = useSelection();
  const viewingPlayer = useViewingPlayerOpt();

  const isSelected = selectedActionSlot === actionSlot;
  const {
    agentIcon,
    effects,
    cost,
    meaples,
    isBattle,
    bonusSpice,
    isControllable,
    id: name,
    controlEffect,
    blockedBy = [],
    controlDreadnought,
    controlFlag,
  } = actionSlot;

  const agentIconSize = 20;
  const withOverlay = Boolean(
    allowOverlay &&
      game &&
      game.currentPlayer.id === viewingPlayer?.id &&
      !onClick
  );

  return (
    <div
      style={{
        width: 180,
        height: 90,
        borderRadius: "8px",
        transition: transition({ durationInMs: Duration.Short }),
        boxShadow: isSelected
          ? `${Colors.selection.selected} 4px 4px 8px 2px`
          : onClick
          ? `${Colors.selection.selectable} 4px 4px 8px 2px`
          : noShadow
          ? "none"
          : "#AF9949 2px 2px 4px",
        backgroundColor: getActionSlotColor(agentIcon),
        padding: Spacing.px8,
        display: "flex",
        justifyContent: "center",
        flexDirection: "column",
        cursor: onClick ? "pointer" : "default",
        zIndex: ZIndex.actionSlot,
        ...style,
      }}
      onClick={onClick}
    >
      {withOverlay && (
        <XOverlay
          style={{
            borderRadius: "8px",
          }}
        />
      )}
      {(isControllable || shownNames.includes(name)) && (
        <XTypography
          style={{
            position: "absolute",
            bottom: 0,
            left: Spacing.px8,
            color: Colors.common.white,
          }}
        >
          {name}
        </XTypography>
      )}
      <div
        style={{
          display: "flex",
          gap: Spacing.px8,
          alignItems: "center",
        }}
      >
        <div>
          <div
            style={{
              position: "absolute",
              top: -2,
              left: -2,
              zIndex: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <XAgentIcon agentIcon={agentIcon} width={agentIconSize} />
            {(cost?.array ?? []).map(({ name, value }) => (
              <XResourceWithAmount
                key={name}
                type={parseEnumOrError(ResourceType, name)}
                amount={value}
                width={agentIconSize}
              />
            ))}
          </div>
          <div
            style={{
              position: "absolute",
              top: 12,
              left: 26,
              zIndex: ZIndex.meaple,
              display: "flex",
            }}
          >
            {meaples.pile.map((meaple) => (
              <XMeaple
                key={meaple.id}
                meaple={meaple}
                noAnimation={noAnimation}
              />
            ))}
          </div>
          <div
            style={{
              borderTopRightRadius: "12px",
              borderBottomLeftRadius: "12px",
              overflow: "hidden",
              border: "1px solid rgba(0, 0, 0, 0.3)",
              position: "relative",
            }}
          >
            <img
              src="/image/actionSlot-dune.png"
              style={{ pointerEvents: "none" }}
            />
            {isBattle && (
              <div style={{ position: "absolute", bottom: 0, right: 0 }}>
                <XCrossedSwordsSvg width={20} />
              </div>
            )}
          </div>
          {isControllable && (
            <div
              style={{
                position: "absolute",
                bottom: 0,
                right: -24,
                width: 24,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
              }}
            >
              {controlEffect && (
                <>
                  <XAgentEffect
                    effectDescription={controlEffect}
                    width={14}
                    context={"actionSlot"}
                  />
                  <XArrowSvg
                    size={4}
                    layout="vertical"
                    style={{ transform: "rotate(-90deg)" }}
                  />
                </>
              )}
              <div
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  width: 24,
                  height: 24,
                  borderTopRightRadius: 2,
                  borderBottomRightRadius: 2,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {controlFlag && (
                  <XPlayerIcon playerId={controlFlag} width={18} />
                )}
                {controlDreadnought && (
                  <XDreadnought
                    width={18}
                    color={
                      Colors.players[
                        players
                          .map(({ id }) => id)
                          .indexOf(controlDreadnought.owner)
                      ].main
                    }
                  />
                )}
              </div>
            </div>
          )}
        </div>
        <div
          style={{
            display: "flex",
            gap: Spacing.px4,
            alignItems: "center",
            flex: 1,
            justifyContent: "center",
          }}
        >
          {effects.map((effect, index) => (
            <XAgentEffect
              key={index}
              effectDescription={effect}
              width={
                name === "Heighliner"
                  ? 18
                  : AgentEffectDescription.isSingle(effect) &&
                    effects.length === 1
                  ? 28
                  : 22
              }
              context="actionSlot"
              mentatOpt={mentatOpt}
              layout="vertical"
            />
          ))}
        </div>
      </div>
      {!!bonusSpice && (
        <div
          style={{
            position: "absolute",
            top: 5,
            right: 5,
            display: "flex",
            alignItems: "center",
          }}
        >
          <XTypography
            style={{ fontSize: FontSize.px12, color: Colors.common.white }}
          >
            bonus: +
          </XTypography>
          <XResourceWithAmount
            amount={bonusSpice}
            type={ResourceType.Spice}
            width={16}
          />
        </div>
      )}
      <div
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          display: "flex",
          padding: Spacing.px4,
        }}
      >
        {oncePerGamePlayedBy.map((player) => (
          <XPlayerIcon key={player.id} width={16} playerId={player.id} />
        ))}
      </div>
      {blockedBy.length > 0 && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
          }}
        >
          {blockedBy.map((playerId) => {
            const playerIndex = (game?.players ?? []).findIndex(
              (player) => player.id === playerId
            );
            const playerColor =
              playerIndex === -1 ? "black" : Colors.players[playerIndex].main;
            return <XLipsSvg key={playerId} width={18} color={playerColor} />;
          })}
        </div>
      )}
    </div>
  );
};
